/**
 * v10 main HUD plate (bottom of screen). 3-section plate + bars-strip + orbs.
 *
 *   ┌──────────────────────────────────────────────────────────────────────┐
 *   │ LEFT (portrait + 3 equip)  │  MID (Q W E R Z)  │  RIGHT (6×2 bag)   │
 *   ├──────────────────────────────────────────────────────────────────────┤
 *   │ ☼ Warmth │  HP / Mana / Stamina bars stack            │ ☐ Hunger    │
 *   └──────────────────────────────────────────────────────────────────────┘
 *
 * Spec: docs/design/02-ui.md §"HUD layout — v10" + docs/superpowers/specs/
 *       2026-05-01-ui-implementation-spec.md §"Per-gap detail".
 *
 * This module owns the entire bottom HUD cluster — replaces the legacy
 * `#hp-hud` (hud.ts), `#inventory-hud` (inventory-hud.ts), `#survival-strip`
 * (survival-strip.ts), and `#inv` resource counters (index.html).
 *
 * Not in this cut (deferred — no schema or out-of-scope this pass):
 *   - Expertise arc (5 trees w/ 5 pips) — needs expertise schema
 *   - Perk-icon strip — needs perk schema
 *   - Ability cooldown sweeps + mana cost / charge counters — needs ability schema
 *   - Auxiliary expertise toggle bar (Adrenaline Rush / Sage Mode) — tier-4 abilities
 *   - Painted V Rising PNG chrome — separate pass per the implementation-methodology doc
 *   - Survival edge-state visuals (Frostbite / Starving / StaminaFaint / Sated) — basic
 *     Normal / Warning / Critical color ramp only
 */
import { ITEMS, isUsable, EQUIPMENT_SLOT_KEYS, BACKPACK_SLOT_COUNT, levelForXp, xpForLevel, ABILITIES, ABILITY_BLURBS, type AbilityDef, type AbilityId, type Cube } from "@tribez/shared";
import { itemIconUrl } from "../items/item-icons";
import { abilityIconUrl } from "./ability-icons";

/** Visual backpack size: 6×2 = 12 per v10 spec. As of 2026-05-03 the schema
 *  also seeds 12 backpack slots (BACKPACK_SLOT_COUNT bumped from 8 → 12 in
 *  inventory.ts), so this constant should always match BACKPACK_SLOT_COUNT
 *  for v1 — the constant is kept here in case a future revision wants to
 *  decouple visual cells from schema slots (e.g. unlockable bag size). */
const VISUAL_BACKPACK_SLOTS = 12;

/** Hotkeys 1..9 map to slots 0..8 (10..12 are mouse-only per v10 spec). */
const KEYBOARD_HOTKEY_SLOTS = 9;

const ABILITY_KEYS = ["Q", "W", "E", "R", "Z"] as const;

/** Equipment-slot hotkeys: the key rendered below each equip slot (and the key
 *  that triggers the active for the item in that slot). Armor = G is intentional
 *  — the G/heal-bear handler in main.ts is the armor-slot active; C and X are
 *  the new main-hand and off-hand actives (Hunting Machete / Obedience Rod). */
const EQUIP_HOTKEYS: Record<string, string> = {
  "main-hand": "C",
  "offhand":   "X",
  "armor":     "G",
};

/** Castable ability keys → their cube.spellbook binding field (Z is the
 *  build-menu opener, not a learnable ability, so it's excluded). */
const SKILL_SLOTS: ReadonlyArray<readonly [string, string]> = [
  ["Q", "slotQ"], ["W", "slotW"], ["E", "slotE"], ["R", "slotR"],
];

/** One-line cost + cooldown summary for the ability hover tooltip. Mirrors the
 *  former standalone ability-bar (now folded into the plate circles). */
function abilityMeta(def: AbilityDef): string {
  const parts: string[] = [];
  const cost = def.cost;
  if (cost.kind === "mana") parts.push(`${cost.amount} mana`);
  else if (cost.kind === "hp-sacrifice") parts.push(`${cost.percentMaxHp}% HP`);
  else parts.push("No cost");
  const t = def.targeting;
  if (t.mode === "toggle") parts.push("Toggle");
  else if ("chargesMax" in t && t.chargesMax) parts.push(`${t.chargesMax} charge${t.chargesMax > 1 ? "s" : ""}`);
  else if (def.cooldownMs > 0) parts.push(`${Math.round(def.cooldownMs / 1000)}s cooldown`);
  return parts.join(" · ");
}

/** Empty-state SVG glyphs for the 3 equipment slots — faint outline of what
 *  the slot accepts, shown when the slot is empty. Hidden via CSS once an
 *  item is equipped. Same viewBox so they share a single CSS rule. */
const EQUIP_EMPTY_GLYPHS: Record<string, string> = {
  // Main-hand: sword silhouette (cross-guard + blade)
  "main-hand": `<svg class="mp-empty-glyph" viewBox="0 0 24 24"><path d="M12 2 L13 14 L11 14 Z M9 14 H15 L15 16 H9 Z M11.2 16 H12.8 V22 H11.2 Z" stroke="currentColor" stroke-width="0.6" fill="currentColor" fill-opacity="0.35"/></svg>`,
  // Offhand: round Viking shield (wood planks + boss)
  "offhand": `<svg class="mp-empty-glyph" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.2" fill="currentColor" fill-opacity="0.18"/><circle cx="12" cy="12" r="2.4" stroke="currentColor" stroke-width="0.8" fill="currentColor" fill-opacity="0.35"/><path d="M12 3 V21 M3.5 7.5 L20.5 16.5 M20.5 7.5 L3.5 16.5" stroke="currentColor" stroke-width="0.5" fill="none" opacity="0.5"/></svg>`,
  // Armor: tunic / chest piece silhouette (shoulder caps + body)
  "armor": `<svg class="mp-empty-glyph" viewBox="0 0 24 24"><path d="M8 4 L12 5 L16 4 L19 7 L17 10 L17 21 L7 21 L7 10 L5 7 Z M12 5 V21" stroke="currentColor" stroke-width="0.8" fill="currentColor" fill-opacity="0.25"/></svg>`,
};

/** SVG glyph for the Z build slot (always-shown — Z is the build-menu opener,
 *  not a learnable ability). Hammer & anvil silhouette. */
const Z_BUILD_GLYPH = `<svg class="mp-skill-build-glyph" viewBox="0 0 24 24"><path d="M12 3 L20 7 L20 11 L4 11 L4 7 Z" stroke="currentColor" stroke-width="0.8" fill="currentColor" fill-opacity="0.4"/><rect x="6" y="11" width="12" height="3" stroke="currentColor" stroke-width="0.8" fill="currentColor" fill-opacity="0.25"/><rect x="9.5" y="14" width="5" height="6" stroke="currentColor" stroke-width="0.8" fill="currentColor" fill-opacity="0.45"/></svg>`;

/** Padlock glyph shown in an EMPTY Q/W/E/R slot — reads as "locked, buy a spell"
 *  (distinct from the dark cooldown disc). Auto-hidden once a spell is bound
 *  (.mp-skill-bound hides .mp-skill-glyph, which contains it). */
const LOCK_GLYPH = `<svg class="mp-skill-lock" viewBox="0 0 24 24"><rect x="5" y="10.5" width="14" height="9.5" rx="1.6" stroke="currentColor" stroke-width="1.4" fill="currentColor" fill-opacity="0.16"/><path d="M8 10.5 V7.5 a4 4 0 0 1 8 0 V10.5" stroke="currentColor" stroke-width="1.4" fill="none"/><circle cx="12" cy="14.6" r="1.5" fill="currentColor"/></svg>`;

/** SVG glyphs for the survival orbs — ported from the v10 mockup (`hud-v10.html`).
 *  Designed to read against the orb's liquid fill (warmth = orange, hunger = yellow). */
const WARMTH_ICON_SVG = `<svg viewBox="0 0 36 36">
  <rect x="5" y="26" width="26" height="4" rx="1.5" fill="#8b6a3d" stroke="#3a1e08" stroke-width="1" transform="rotate(-8 18 28)"/>
  <rect x="5" y="26" width="26" height="4" rx="1.5" fill="#5a3f1e" stroke="#2a1510" stroke-width="1" transform="rotate(8 18 28)"/>
  <path d="M18 3 C23 9 27 14 27 20 C27 25 23 28 18 28 C13 28 9 25 9 20 C9 15 14 13 14 8 C14 6 16 5 18 3 Z"
        fill="#fff" stroke="#8a2a00" stroke-width="1.4"/>
  <path d="M18 13 C21 16 22 19 22 22 C22 25 20 26 18 26 C16 26 14 25 14 22 C14 20 16 17 18 13 Z"
        fill="#ffcf74"/>
</svg>`;

const HUNGER_ICON_SVG = `<svg viewBox="0 0 36 36">
  <circle cx="28" cy="7" r="3.2" fill="#fff8e6" stroke="#4a3510" stroke-width="1.2"/>
  <circle cx="31" cy="9" r="2.6" fill="#fff8e6" stroke="#4a3510" stroke-width="1.2"/>
  <line x1="27" y1="10" x2="19" y2="18" stroke="#fff8e6" stroke-width="4.5" stroke-linecap="round"/>
  <line x1="27" y1="10" x2="19" y2="18" stroke="#4a3510" stroke-width="1" fill="none"/>
  <ellipse cx="15" cy="23" rx="11" ry="10" fill="#fff" stroke="#4a1810" stroke-width="1.5" transform="rotate(-18 15 23)"/>
  <path d="M7 19 Q12 16 17 19" stroke="#b85a28" stroke-width="1.2" fill="none"/>
  <path d="M10 26 Q16 23 21 28" stroke="#b85a28" stroke-width="1.2" fill="none"/>
  <path d="M14 31 Q18 30 22 31" stroke="#b85a28" stroke-width="1" fill="none"/>
</svg>`;

export interface MainPlate {
  /** Mirror schema state into the plate. Call each frame from main.ts. */
  refresh(cube: Cube): void;
  /** Cleanup (remove DOM, drop listeners). */
  destroy(): void;
}

type SendFn = (type: string, payload: unknown) => void;

interface InvCellRefs {
  el: HTMLDivElement;
  iconEl: HTMLImageElement;
  countEl: HTMLDivElement;
}

/**
 * @param send  Wire-send fn (forwards to room.send).
 * @param isAnyModalOpen  Predicate: returns true while any other modal is open.
 *   Suppresses backpack hotkeys 1..9 so a press doesn't fire `use` while the
 *   build menu / craft menu / building menu / corpse loot is open.
 * @param onZPressed  Called when the Z ability slot is clicked (mirrors the
 *   `Z` keybind to open the build-menu shelf). Optional — keyboard `Z` is
 *   handled in main.ts.
 * @param onRecenter  Called when the portrait is clicked → recenter the camera
 *   on the local hero (issue #5 recovery affordance; mirrors the Space keybind).
 */
export function createMainPlate(
  send: SendFn,
  isAnyModalOpen?: () => boolean,
  onZPressed?: () => void,
  onRecenter?: () => void,
): MainPlate {
  const root = document.createElement("div");
  root.id = "main-plate";

  // Plate (3 sections + dividers)
  const plate = document.createElement("div");
  plate.className = "mp-plate";
  root.appendChild(plate);

  // === LEFT: portrait + equip row ============================================
  const leftSection = document.createElement("div");
  leftSection.className = "mp-section mp-section-left";
  plate.appendChild(leftSection);

  // Portrait + XP ring + level badge
  const portraitWrap = document.createElement("div");
  portraitWrap.className = "mp-portrait";
  portraitWrap.innerHTML = `
    <svg class="mp-xp-ring" viewBox="0 0 126 126">
      <circle cx="63" cy="63" r="59" fill="none" stroke="#1a1f2a" stroke-width="6"/>
      <circle class="mp-xp-fill" cx="63" cy="63" r="59" fill="none" stroke="#f7c360"
              stroke-width="6" stroke-dasharray="370.7" stroke-dashoffset="370.7"
              transform="rotate(-90 63 63)" stroke-linecap="round"/>
    </svg>
    <div class="mp-portrait-inner">
      <img class="mp-portrait-img" src="/assets/ui/portrait-default.png" alt="" />
    </div>
    <div class="mp-portrait-lvl">1</div>
  `;
  leftSection.appendChild(portraitWrap);
  const xpFillEl = portraitWrap.querySelector<SVGCircleElement>(".mp-xp-fill")!;
  const lvlBadgeEl = portraitWrap.querySelector<HTMLDivElement>(".mp-portrait-lvl")!;

  // Click portrait → recenter camera on hero (issue #5). A discoverable,
  // always-visible "return home" target (Dota convention); mirrors Space.
  if (onRecenter) {
    portraitWrap.style.cursor = "pointer";
    portraitWrap.style.pointerEvents = "auto";
    portraitWrap.title = "Recenter camera on your hero (Space)";
    portraitWrap.addEventListener("click", () => onRecenter());
  }

  // Equipment row (3 circular slots)
  const equipGrid = document.createElement("div");
  equipGrid.className = "mp-equip-grid";
  leftSection.appendChild(equipGrid);

  const equipCells = new Map<string, InvCellRefs>();
  for (const slotKey of EQUIPMENT_SLOT_KEYS) {
    const refs = makeInvCell("equip", slotKey, EQUIP_HOTKEYS[slotKey]);
    equipCells.set(slotKey, refs);
    equipGrid.appendChild(refs.el);
  }

  // === Divider 1 =============================================================
  const div1 = document.createElement("div");
  div1.className = "mp-divider";
  plate.appendChild(div1);

  // === MIDDLE: ability sub-panel (Q/W/E/R/Z) =================================
  const midSection = document.createElement("div");
  midSection.className = "mp-section mp-section-mid";
  plate.appendChild(midSection);

  const skillsWrap = document.createElement("div");
  skillsWrap.className = "mp-skills-wrap";
  midSection.appendChild(skillsWrap);

  // .mp-skill cells are direct children of .mp-skills-wrap (no .mp-skills
  // flex wrapper) so the `.mp-skills-wrap > .mp-skill:nth-child(N)` locked
  // CSS selectors actually match — they're absolute-positioned directly off
  // the painted ability sub-panel.
  interface SkillRefs { el: HTMLDivElement; icon: HTMLDivElement; cd: HTMLDivElement }
  const skillCells = new Map<string, SkillRefs>();
  for (const key of ABILITY_KEYS) {
    const slot = document.createElement("div");
    slot.className = `mp-skill mp-skill-${key.toLowerCase()}`;
    if (key === "Z") {
      // Z gets a hammer/anvil glyph (always shown — Z is the build-menu trigger,
      // not a learnable ability).
      slot.classList.add("mp-skill-build");
      slot.innerHTML = `${Z_BUILD_GLYPH}<span class="mp-skill-key">${key}</span>`;
      if (onZPressed) {
        slot.style.cursor = "pointer";
        slot.style.pointerEvents = "auto";
        slot.addEventListener("click", () => onZPressed());
      }
    } else {
      // Q/W/E/R: faint .mp-skill-glyph placeholder when empty; .mp-skill-icon
      // (the coloured ability tile) + .mp-skill-cd (cooldown disc) fill in once
      // a spell is bound — driven from cube.spellbook in refresh().
      slot.innerHTML = `
        <div class="mp-skill-glyph">${LOCK_GLYPH}</div>
        <div class="mp-skill-icon"></div>
        <div class="mp-skill-cd"></div>
        <span class="mp-skill-key">${key}</span>
      `;
      const icon = slot.querySelector<HTMLDivElement>(".mp-skill-icon")!;
      const cd = slot.querySelector<HTMLDivElement>(".mp-skill-cd")!;
      skillCells.set(key, { el: slot, icon, cd });
      // Hover tooltip (name + cost/cooldown + blurb) — reuses #inv-tooltip.
      slot.style.pointerEvents = "auto";
      slot.addEventListener("mouseenter", (ev) => showAbilityTooltip(slot, ev as MouseEvent));
      slot.addEventListener("mousemove", (ev) => positionTooltip(ev as MouseEvent));
      slot.addEventListener("mouseleave", () => { tooltip!.style.display = "none"; });
    }
    skillsWrap.appendChild(slot);
  }
  const buildSlot = skillsWrap.querySelector<HTMLDivElement>(".mp-skill-build")!;

  // === Divider 2 =============================================================
  const div2 = document.createElement("div");
  div2.className = "mp-divider";
  plate.appendChild(div2);

  // === RIGHT: backpack (6×2 = 12 cells) ======================================
  const rightSection = document.createElement("div");
  rightSection.className = "mp-section mp-section-right";
  plate.appendChild(rightSection);

  const backpack = document.createElement("div");
  backpack.className = "mp-backpack";
  rightSection.appendChild(backpack);

  const backpackCells = new Map<string, InvCellRefs>();
  for (let i = 0; i < VISUAL_BACKPACK_SLOTS; i++) {
    const slotKey = String(i);
    // Every slot displays its 1-indexed position label. Slots 0..8 (labels
    // "1".."9") are also wired to keyboard hotkeys 1-9 in onKeydown — slots
    // 9..11 (labels "10", "11", "12") are mouse-only per v10 spec but still
    // show the label for visual reference.
    const label = String(i + 1);
    const refs = makeInvCell("backpack", slotKey, label);
    if (i >= BACKPACK_SLOT_COUNT) {
      // Schema-side slot doesn't exist yet (pre-migration); render but mark
      // `data-schema="absent"` so callers know not to expect content.
      refs.el.dataset.schema = "absent";
    }
    backpackCells.set(slotKey, refs);
    backpack.appendChild(refs.el);
  }

  // === BARS-STRIP: warmth orb | HP/Mana/Stamina | hunger orb =================
  const barsStrip = document.createElement("div");
  barsStrip.className = "mp-bars-strip";
  root.appendChild(barsStrip);

  const warmthOrb = makeOrb("warmth", "Warmth", "Keep above 0 — freezing damages you and stops you running. Stay near a fire or carry a torch.");
  barsStrip.appendChild(warmthOrb.el);

  // Bars are direct children of .mp-bars-strip so each can be positioned
  // independently against the painted bar frames (no flex-stack distributes
  // their height as a group). Layout-editor exposes each as its own group.
  const hpRow = makeBar("hp", "HP", "Health — reaches zero and you die; recovers slowly out of combat.");
  const manaRow = makeBar("mana", "Mana", "Mana — fuel for spells and abilities; regenerates passively over time.");
  const stamRow = makeBar("stam", "Stamina", "Stamina — drains while you sprint; stand still or walk (V, ×0.6 speed) to recover.");
  barsStrip.appendChild(hpRow.el);
  barsStrip.appendChild(manaRow.el);
  barsStrip.appendChild(stamRow.el);

  const hungerOrb = makeOrb("hunger", "Hunger", "Well-Fed at 50+ gives +10% attack damage. Eat cooked food to stay fed — going hungry never harms you.");
  barsStrip.appendChild(hungerOrb.el);

  document.body.appendChild(root);

  // === Tooltip ==============================================================
  // Reuse the existing #inv-tooltip styles from style.css. Created once if
  // not already present (defensive: another module may own it).
  let tooltip = document.getElementById("inv-tooltip") as HTMLDivElement | null;
  if (!tooltip) {
    tooltip = document.createElement("div");
    tooltip.id = "inv-tooltip";
    tooltip.style.display = "none";
    document.body.appendChild(tooltip);
  }

  // === Backpack hotkey listener =============================================
  // Looks up the cell's current itemId via DOM dataset (set by setCell) so a
  // hotkey on a material slot is silently ignored — same `isUsable` gate as
  // left-click. Server `handleUse` re-checks defensively.
  function onKeydown(e: KeyboardEvent): void {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (isAnyModalOpen?.()) return;
    const n = parseInt(e.key, 10);
    if (!Number.isInteger(n) || n < 1 || n > KEYBOARD_HOTKEY_SLOTS) return;
    const slotKey = String(n - 1);
    if (Number(slotKey) >= BACKPACK_SLOT_COUNT) return; // schema-absent slot
    const cell = backpackCells.get(slotKey);
    if (!cell) return;
    const itemId = cell.el.dataset.itemid;
    if (!itemId) return;
    const def = ITEMS[itemId];
    if (!def || !isUsable(def)) return;
    send("use", { fromBackpackSlot: slotKey });
  }
  document.addEventListener("keydown", onKeydown);

  // === Cell factory =========================================================
  function makeInvCell(kind: "equip" | "backpack", slotKey: string, hotkey?: string): InvCellRefs {
    const el = document.createElement("div");
    el.className = `mp-cell mp-cell-${kind}`;
    el.dataset.kind = kind;
    el.dataset.slot = slotKey;
    if (hotkey) el.dataset.hotkey = hotkey;
    // Equip cells get an empty-state SVG glyph (sword/shield/armor) — hidden
    // by CSS once an item icon is set. Backpack cells stay glyphless.
    const emptyGlyph = kind === "equip" ? (EQUIP_EMPTY_GLYPHS[slotKey] ?? "") : "";
    el.innerHTML = `
      <div class="mp-cell-hk">${hotkey ?? ""}</div>
      ${emptyGlyph}
      <img class="mp-cell-icon" alt="" />
      <div class="mp-cell-count"></div>
    `;
    const iconEl = el.querySelector<HTMLImageElement>(".mp-cell-icon")!;
    const countEl = el.querySelector<HTMLDivElement>(".mp-cell-count")!;

    // Click → equip / unequip / use, mirrored from inventory-hud.ts so the
    // wire contract is unchanged for the server. Materials (Iron Ore, Lumber,
    // Stone, Leather, Wool, …) share `slot: "consumable"` with real food in
    // the KTS extraction but have no declared effects; `isUsable` rejects
    // them so left-click is inert (the previous gate would have routed them
    // through the `use` route and silently decremented the stack).
    el.addEventListener("click", () => {
      const itemId = el.dataset.itemid;
      if (!itemId) return;
      const def = ITEMS[itemId];
      if (!def) return;
      if (kind === "equip") {
        send("unequip", { fromEquipSlot: slotKey });
      } else if (def.slot === "main-hand" || def.slot === "offhand" || def.slot === "armor") {
        send("equip", { fromBackpackSlot: slotKey, toEquipSlot: def.slot });
      } else if (isUsable(def)) {
        send("use", { fromBackpackSlot: slotKey });
      }
      // else: material — no left-click action.
    });

    el.addEventListener("contextmenu", (ev) => {
      ev.preventDefault();
      if (kind !== "backpack") return;
      const itemId = el.dataset.itemid;
      if (!itemId) return;
      send("drop", { fromBackpackSlot: slotKey });
    });

    el.addEventListener("mouseenter", (ev) => showTooltip(el, ev as MouseEvent));
    el.addEventListener("mousemove", (ev) => positionTooltip(ev as MouseEvent));
    el.addEventListener("mouseleave", () => { tooltip!.style.display = "none"; });

    return { el, iconEl, countEl };
  }

  function makeOrb(kind: "warmth" | "hunger", label: string, desc: string) {
    const el = document.createElement("div");
    el.className = `mp-orb-wrap mp-orb-${kind}`;
    const icon = kind === "warmth" ? WARMTH_ICON_SVG : HUNGER_ICON_SVG;
    el.innerHTML = `
      <div class="mp-orb">
        <div class="mp-orb-fill"></div>
        <div class="mp-orb-glyph">${icon}</div>
      </div>
      <div class="mp-orb-value">100</div>
    `;
    // Orbs get the same hover tooltip as bars (makeBar) — previously absent, so
    // there was nowhere on the HUD that explained Well-Fed / warmth.
    el.addEventListener("mouseenter", (ev) => {
      tooltip!.innerHTML = `<div class="inv-tt__name">${label}</div><div class="inv-tt__effects">${desc}</div>`;
      tooltip!.style.display = "block";
      positionTooltip(ev as MouseEvent);
    });
    el.addEventListener("mousemove", (ev) => positionTooltip(ev as MouseEvent));
    el.addEventListener("mouseleave", () => { tooltip!.style.display = "none"; });
    return {
      el,
      fill: el.querySelector<HTMLDivElement>(".mp-orb-fill")!,
      orb: el.querySelector<HTMLDivElement>(".mp-orb")!,
      value: el.querySelector<HTMLDivElement>(".mp-orb-value")!,
    };
  }

  function makeBar(kind: "hp" | "mana" | "stam", label: string, desc: string) {
    const el = document.createElement("div");
    el.className = `mp-bar-row mp-bar-${kind}`;
    el.innerHTML = `
      <div class="mp-bar-fill"></div>
      <div class="mp-bar-text">0 / 0</div>
    `;
    el.addEventListener("mouseenter", (ev) => {
      tooltip!.innerHTML = `<div class="inv-tt__name">${label}</div><div class="inv-tt__effects">${desc}</div>`;
      tooltip!.style.display = "block";
      positionTooltip(ev as MouseEvent);
    });
    el.addEventListener("mousemove", (ev) => positionTooltip(ev as MouseEvent));
    el.addEventListener("mouseleave", () => { tooltip!.style.display = "none"; });
    return {
      el,
      fill: el.querySelector<HTMLDivElement>(".mp-bar-fill")!,
      text: el.querySelector<HTMLDivElement>(".mp-bar-text")!,
    };
  }

  function fmtStat(label: string, value: number | undefined): string {
    if (value === undefined || value === 0) return "";
    const sign = value > 0 ? "+" : "";
    return `<div class="inv-tt__stat">${label}: ${sign}${value}</div>`;
  }

  function showTooltip(cell: HTMLDivElement, ev: MouseEvent): void {
    const itemId = cell.dataset.itemid;
    if (!itemId) { tooltip!.style.display = "none"; return; }
    const def = ITEMS[itemId];
    if (!def) { tooltip!.style.display = "none"; return; }
    const s = def.stats;
    const statsHtml =
      fmtStat("STR", s.strength) + fmtStat("DEX", s.dexterity) + fmtStat("END", s.endurance) +
      fmtStat("INT", s.intelligence) + fmtStat("WIL", s.willpower) +
      fmtStat("Physique", s.physique) + fmtStat("Wit", s.wit) +
      fmtStat("Armor", s.armor) + fmtStat("S.Armor", s.sArmor) + fmtStat("Armor Pen", s.armorPen) +
      fmtStat("HP Max", s.hpMax) + fmtStat("HP Regen", s.hpRegen) +
      fmtStat("Mana Max", s.manaMax) + fmtStat("Mana Regen", s.manaRegen) +
      fmtStat("Crit Chance", s.critChance) + fmtStat("Crit Damage", s.critDamage) +
      fmtStat("Atk Range", s.attackRange) + fmtStat("Atk Period", s.attackPeriod) +
      fmtStat("Move Speed", s.moveSpeedMultiplier) +
      fmtStat("Bonus vs Animal", s.attrBonusAnimal) + fmtStat("Bonus vs Tree", s.attrBonusTree) +
      fmtStat("Bonus vs Structure", s.attrBonusStructure) + fmtStat("Bonus vs Hero", s.attrBonusHero);

    const effectsHtml = def.pendingEffects.length > 0
      ? `<div class="inv-tt__effects">Effects: ${def.pendingEffects.join(", ")}</div>` : "";

    tooltip!.innerHTML = `
      <div class="inv-tt__name">${def.displayName}</div>
      <div class="inv-tt__meta">${def.slot} · ${def.source}</div>
      ${statsHtml || `<div class="inv-tt__empty">(no stat mods)</div>`}
      ${effectsHtml}
    `;
    tooltip!.style.display = "block";
    positionTooltip(ev);
  }

  function showAbilityTooltip(cell: HTMLDivElement, ev: MouseEvent): void {
    const id = cell.dataset.ability;
    if (!id) { tooltip!.style.display = "none"; return; }
    const def = ABILITIES[id as AbilityId];
    if (!def) { tooltip!.style.display = "none"; return; }
    const blurb = ABILITY_BLURBS[id] ?? "";
    tooltip!.innerHTML = `
      <div class="inv-tt__name">${def.name}</div>
      <div class="inv-tt__meta">${abilityMeta(def)}</div>
      ${blurb ? `<div class="inv-tt__effects">${blurb}</div>` : ""}
    `;
    tooltip!.style.display = "block";
    positionTooltip(ev);
  }

  function positionTooltip(ev: MouseEvent): void {
    const offsetX = 14, offsetY = 14;
    const ttRect = tooltip!.getBoundingClientRect();
    let x = ev.clientX + offsetX;
    let y = ev.clientY + offsetY;
    if (x + ttRect.width > window.innerWidth) x = ev.clientX - ttRect.width - offsetX;
    if (y + ttRect.height > window.innerHeight) y = ev.clientY - ttRect.height - offsetY;
    tooltip!.style.left = `${x}px`;
    tooltip!.style.top = `${y}px`;
  }

  function setCell(refs: InvCellRefs, itemId: string, count: number): void {
    if (!itemId) {
      refs.el.classList.add("mp-cell-empty");
      delete refs.el.dataset.itemid;
      refs.iconEl.removeAttribute("src");
      refs.iconEl.style.visibility = "hidden";
      refs.countEl.textContent = "";
      return;
    }
    refs.el.classList.remove("mp-cell-empty");
    refs.el.dataset.itemid = itemId;
    refs.iconEl.style.visibility = "visible";
    refs.iconEl.src = itemIconUrl(itemId);
    refs.countEl.textContent = count > 1 ? String(count) : "";
  }

  // === Survival meter level ramps ============================================
  // Per-meter framing (2026-06-29, positive-not-punitive): only WARMTH is lethal,
  // so only warmth keeps the danger ramp (warning → critical → edge red pulse).
  // Hunger is a pure-positive gauge — gold "Well-Fed" cue at ≥50, never alarms.
  // Stamina at 0 just means "can't run" — a calm amber when low, never a red
  // death-pulse.
  function warmthTier(v: number): "normal" | "warning" | "critical" | "edge" {
    if (v <= 0) return "edge";
    if (v < 25) return "critical";
    if (v < 50) return "warning";
    return "normal";
  }
  // Hunger ≥ 50 = Well-Fed (+10% attack) → gold "fed" cue; below = neutral, no alarm.
  function hungerTier(v: number): "fed" | "normal" {
    return v >= 50 ? "fed" : "normal";
  }
  // Stamina: low = calm amber ("winded"); 0 = can't run (still amber, no pulse).
  // Never escalates to the critical/edge red death-pulse.
  function staminaTier(v: number): "normal" | "warning" {
    return v < 25 ? "warning" : "normal";
  }

  // Backpack snapshot for +N gather floaters: last seen {itemId,count} per slot.
  // Empty until the first refresh primes it, so initial sync never floats.
  const lastBackpack = new Map<string, { itemId: string; count: number }>();

  /** Float a small "+N" above a backpack cell when its stack grows (gather/loot).
   *  Body-anchored because the cell clips overflow. */
  function floatGain(cellEl: HTMLElement, n: number): void {
    const rect = cellEl.getBoundingClientRect();
    if (rect.width === 0) return; // cell not laid out yet
    const g = document.createElement("div");
    g.className = "mp-gain";
    g.textContent = `+${n}`;
    g.style.left = `${rect.left + rect.width / 2}px`;
    g.style.top = `${rect.top}px`;
    document.body.appendChild(g);
    void g.offsetWidth;
    g.classList.add("rise");
    setTimeout(() => g.remove(), 900);
  }

  // === Refresh ==============================================================
  function refresh(cube: Cube): void {
    // HP
    const hpPct = cube.hpMax > 0 ? (cube.hp / cube.hpMax) * 100 : 0;
    hpRow.fill.style.width = `${hpPct}%`;
    hpRow.text.textContent = `${cube.hp} / ${cube.hpMax}`;

    // Mana — reads cube.spellbook.mana (transient, written by regen + cast costs).
    // `spellbook` may be undefined on the Colyseus client before the first delta
    // containing a Spellbook sub-schema arrives — same race the survival block
    // handles below. Default to 0.
    const mana = cube.spellbook?.mana ?? 0;
    const manaPct = cube.manaMax > 0 ? (mana / cube.manaMax) * 100 : 0;
    manaRow.fill.style.width = `${manaPct}%`;
    manaRow.text.textContent = `${mana} / ${cube.manaMax}`;

    // Stamina (0..100) — cube.survival may be undefined on Colyseus client before
    // the first delta containing a SurvivalBag sub-schema arrives; default to 100.
    const survival = cube.survival;
    const stam = survival?.stamina ?? 100;
    stamRow.fill.style.width = `${stam}%`;
    stamRow.text.textContent = `${stam} / 100`;
    applyTierClass(stamRow.el, staminaTier(stam));

    // Warmth orb
    const warmth = survival?.warmth ?? 100;
    warmthOrb.fill.style.height = `${warmth}%`;
    warmthOrb.value.textContent = String(warmth);
    applyTierClass(warmthOrb.orb, warmthTier(warmth));

    // Hunger orb
    const hunger = survival?.hunger ?? 100;
    hungerOrb.fill.style.height = `${hunger}%`;
    hungerOrb.value.textContent = String(hunger);
    applyTierClass(hungerOrb.orb, hungerTier(hunger));

    // XP ring + level badge — real KTS curve (B5 progression). Ring fills across
    // the current level's XP band; badge shows the synced character level. Hover
    // surfaces unspent expertise points (the spend UI lands in the Phase-2 arc).
    const xp = cube.xp ?? 0;
    const lvl = cube.expertise?.characterLevel ?? levelForXp(xp);
    const bandFloor = xpForLevel(lvl);
    const bandCeil = xpForLevel(lvl + 1);
    const xpProgress = bandCeil > bandFloor ? Math.max(0, Math.min(1, (xp - bandFloor) / (bandCeil - bandFloor))) : 1;
    const dashLen = 370.7; // matches stroke-dasharray
    xpFillEl.setAttribute("stroke-dashoffset", String(dashLen * (1 - xpProgress)));
    lvlBadgeEl.textContent = String(lvl);
    const banked = cube.expertise?.bankedPoints ?? 0;
    lvlBadgeEl.title = banked > 0
      ? `Level ${lvl} — ${banked} expertise point${banked === 1 ? "" : "s"} to spend`
      : `Level ${lvl}`;

    // Equipment cells — cube.equipment may be undefined before first Colyseus delta
    for (const slotKey of EQUIPMENT_SLOT_KEYS) {
      const slot = cube.equipment?.get(slotKey);
      const refs = equipCells.get(slotKey);
      if (!refs) continue;
      setCell(refs, slot?.itemId ?? "", 1);
    }

    // Backpack cells — cube.backpack may be undefined before first Colyseus delta
    for (let i = 0; i < VISUAL_BACKPACK_SLOTS; i++) {
      const key = String(i);
      const refs = backpackCells.get(key);
      if (!refs) continue;
      if (i < BACKPACK_SLOT_COUNT) {
        const slot = cube.backpack?.get(key);
        const itemId = slot?.itemId ?? "";
        const count = slot?.count ?? 0;
        // +N gather floater: float when a stack of the SAME item grows. Only
        // after the slot was primed (prev defined) so the initial load is silent;
        // same-item guard avoids false floats from equip/move shuffles.
        const prev = lastBackpack.get(key);
        if (prev && itemId && prev.itemId === itemId && count > prev.count) {
          floatGain(refs.el, count - prev.count);
        }
        lastBackpack.set(key, { itemId, count });
        setCell(refs, itemId, count);
      } else {
        setCell(refs, "", 0);
      }
    }

    // Ability circles Q/W/E/R — mirror cube.spellbook bindings into the painted
    // bezels (Z stays the build glyph). `spellbook` / `cooldowns` may be
    // undefined on the Colyseus client before their sub-schema deltas arrive.
    const sb = cube.spellbook;
    const nowMs = Date.now();
    for (const [key, field] of SKILL_SLOTS) {
      const refs = skillCells.get(key);
      if (!refs) continue;
      const id = sb ? (sb as unknown as Record<string, string>)[field] : "";
      if (!id) {
        refs.el.classList.remove("mp-skill-bound");
        delete refs.el.dataset.ability;
        refs.cd.style.display = "none";
        continue;
      }
      const def = ABILITIES[id as AbilityId];
      refs.el.classList.add("mp-skill-bound");
      refs.el.dataset.ability = id;
      // Real ability art (V Rising-style, colour-coded) drawn as a cover
      // background; the circular .mp-skill-icon clips it to the disc.
      refs.icon.style.backgroundImage = `url("${abilityIconUrl(id as AbilityId)}")`;
      refs.icon.textContent = "";
      const expiry = sb?.cooldowns?.get(def?.cooldownKey ?? id);
      if (expiry !== undefined && expiry > nowMs) {
        refs.cd.style.display = "flex";
        refs.cd.textContent = ((expiry - nowMs) / 1000).toFixed(1);
      } else {
        refs.cd.style.display = "none";
      }
    }

    // Z slot active state mirrors the build-menu open state via classList —
    // main.ts can flip this if needed; for now no-op (build slot is always
    // available when menu closed).
    void buildSlot;
  }

  function applyTierClass(el: HTMLElement, tier: "normal" | "warning" | "critical" | "edge" | "fed"): void {
    el.classList.remove("mp-tier-normal", "mp-tier-warning", "mp-tier-critical", "mp-tier-edge", "mp-tier-fed");
    el.classList.add(`mp-tier-${tier}`);
  }

  function destroy(): void {
    document.removeEventListener("keydown", onKeydown);
    root.remove();
  }

  return { refresh, destroy };
}
